import { ContactLookupSettings } from "@signalist/shared-types";

import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { SelectField, SelectFieldValue } from "@/components/SelectField/SelectField";

import { PreviewCreditConfirm } from "./PreviewCreditConfirm";

export type PreviewCompanyCount = 10 | 20 | 50;

interface PreviewActivateProps {
	/** The list's contact lookup settings, quoted as part of what the preview runs. */
	contactLookups: ContactLookupSettings;
	/** Starts the preview; rejects when the API refuses it, which the confirm then shows. */
	onStart: (companyCount: PreviewCompanyCount) => Promise<unknown>;
	isLoading: boolean;
	disabled?: boolean;
}

const COMPANY_COUNT_OPTIONS = [
	{ title: "10 companies", value: 10 },
	{ title: "20 companies", value: 20 },
	{ title: "50 companies", value: 50 },
];

/**
 * The list builder's "Preview your list" card. Start Preview opens the credit-confirm modal with
 * the companies the preview searches and what they cost; the preview starts only from there
 * (PREC-4263; it started at once beside a grey "This will use N credit(s)" before).
 */
export const PreviewActivate: FunctionComponent<PreviewActivateProps> = ({
	contactLookups,
	onStart,
	isLoading,
	disabled,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) => t(`list.preview.${key}`, options);
	const [companyCount, setCompanyCount] = useState<SelectFieldValue>(20);
	const [isConfirming, setIsConfirming] = useState(false);

	return (
		<div className="mt-6 rounded-lg border border-border-light bg-brand-white p-6 overflow-visible">
			<div className="flex items-center gap-3 mb-2">
				<div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-action/10">
					<svg className="h-5 w-5 text-blue-action" fill="currentColor" viewBox="0 0 20 20">
						<path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
					</svg>
				</div>
				<h3 className="text-base font-semibold text-text">{ts("title")}</h3>
			</div>
			<p className="text-sm text-text-meta mb-4 ml-[52px]">{ts("description")}</p>
			<div className="flex items-center gap-4 ml-[52px]">
				{/* Above the sibling cards below (for the inline menu) but below the page footer (z-[999]). */}
				<div className="relative z-20 w-48">
					<SelectField
						handleSelect={(value) => setCompanyCount(value)}
						hideEmptyValue
						options={COMPANY_COUNT_OPTIONS}
						placeholder={ts("selectCompanyCount")}
						selectedOption={companyCount}
					/>
				</div>
				<div className="shrink-0">
					<Button
						disabled={disabled}
						isLoading={isLoading}
						title={ts("startPreview")}
						onClick={() => setIsConfirming(true)}
					/>
				</div>
			</div>
			{isConfirming && (
				<PreviewCreditConfirm
					companyCount={companyCount as PreviewCompanyCount}
					contactLookups={contactLookups}
					onClose={() => setIsConfirming(false)}
					onConfirm={async () => {
						await onStart(companyCount as PreviewCompanyCount);
						setIsConfirming(false);
					}}
				/>
			)}
		</div>
	);
};
